import React, { forwardRef, useImperativeHandle, useState } from "react";

import useDate from "core/hooks/useDate";

import commissionService from "core/services/apiServices/commissionService";

import Form from "core/ui-kit/form";

import isFormChanged from "core/helpers/form/isFormChanged";

import EditForm from "./form";
import OverlappedCommissions from "../overlappedCommissions";
import { formatDateRangePickerValues } from "../helpers";
import {
    COMMISSION_ADD_FIELDS,
    commissionPlanStatuses,
    COMMISSION_EDIT_FIELDS
} from "../constants";

const CommissionsPopupEdit = forwardRef(({
    affiliateId,
    onCommissionEdited,
    selectedCommission,
    setEditingDisabled,
    setFormIsChanged,
    setSelectedCommission,
}, ref) => {

    const [formInstance] = Form.useForm();
    const {setFieldsValue} = formInstance;

    const {dateService} = useDate();

    const [overlappedCommissions, setOverlappedCommissions] = useState([]);

    const { startDate, endDate, affiliateCommissonPlanStatus } = selectedCommission;

    const isActive = affiliateCommissonPlanStatus === commissionPlanStatuses.Active;
    const isScheduled = affiliateCommissonPlanStatus === commissionPlanStatuses.Scheduled;

    const onSaveButtonClick = (values) => {
        const { fromValue, toValue } = formatDateRangePickerValues(values[COMMISSION_EDIT_FIELDS.START_AND_END_DATE] || [], dateService);
        const params = {
            id: selectedCommission.id,
            affiliateCommissionPlanId: selectedCommission.affiliateCommissionPlanId,
            affiliateId: affiliateId,
            startDate: fromValue,
            endDate: toValue,
            isEdit: true,
        };

        commissionService.editCommission(params)
            .then(() => {
                onCommissionEdited();
            }).finally(() => {});
    }

    const onSubmit = () => {
        formInstance.validateFields().then(onSaveButtonClick)
    }

    const validationPassedAsValid = () => {
        setOverlappedCommissions([]);
        setEditingDisabled(false);
    }

    const validateConflicts = (currentValues) => {
        const {fromValue, toValue} = formatDateRangePickerValues(currentValues?.startAndEndDate || [], dateService);

        const params = {
            affiliateCommissionPlanId: selectedCommission.affiliateCommissionPlanId,
            [COMMISSION_ADD_FIELDS.COMMISSION]: selectedCommission.id,
            affiliateId,
            isEdit: true,
            [COMMISSION_ADD_FIELDS.START_DATE]: fromValue,
            [COMMISSION_ADD_FIELDS.END_DATE]: toValue || null,
        };

        commissionService.validateCommissionAssignment(params)
            .then((response) => {
                const conflicts = response?.value || [];
                if (conflicts.length > 0) {
                    setEditingDisabled(true);
                    setOverlappedCommissions(conflicts);
                } else {
                    validationPassedAsValid();
                }
            })
            .finally(() => {
                console.log('finally');
            })
    }

    const onFormValuesChange = (initialValues) => {
        const currentValues = formInstance.getFieldsValue();
        const isChanged = isFormChanged(currentValues, initialValues);

        setFormIsChanged(isChanged);

        if (currentValues?.startAndEndDate?.[0]) {
            validateConflicts(currentValues);
        }
    }

    const clearOverlappedCommissionsErrors = () => {
        setFieldsValue({ startAndEndDate: [] });
        setEditingDisabled(false);
        setSelectedCommission(null);
        setOverlappedCommissions([]);
    }

    useImperativeHandle(ref, () => {
        return {
            onSaveButtonClick: () => {
                onSubmit();
            },
        }
    }, [onSubmit]);

    return (
        <div className="rt--commissions-edit">
            {
                overlappedCommissions.length > 0 && (
                    <OverlappedCommissions
                        overlappedCommissions={overlappedCommissions}
                        clearOverlappedCommissionsErrors={clearOverlappedCommissionsErrors}
                    />
                )
            }
            <EditForm
                formInstance={formInstance}
                startDate={startDate}
                endDate={endDate}
                isActive={isActive}
                isScheduled={isScheduled}
                onFormValuesChange={onFormValuesChange}
            />
        </div>
    )
})

export default CommissionsPopupEdit;